// brushPanelShared — Modern「ブラシ」パネルの 2 バリアント (`ModernBrushPanel` = デスクトップ
// タブ版 / `ModernBrushPanelFlat` = フォン版) が共有する純粋部分.
//
// 背景 (docs/_wip/modern-brush-panel-2026-08-17.md §14「タブ構成」consult 2026-08-18):
//   デスクトップは §14 でタブ構成に作り直すが、**フォン (`ModernPhoneShell`) はタブを
//   持ち込まない** (§14 実装注記「シートにはタブを持ち込まないこと」、`ModernPhoneShell`
//   のボトムシートは下部ナビと合わせて既に「開いて選ぶ」2段 UI なので、タブを足すと
//   3段ナビになり縦溢れ問題が悪化する)。フォンは**従来どおり全ブラシを並べる**形を凍結
//   維持する必要があるため、`ModernBrushPanelFlat.tsx` に**旧実装をそのまま残す**。
//
//   両バリアントは「item (= 1 ブラシ/preset のボタン) の見た目」「サムネ計算」「item
//   (リスト表示固定、2026-08-19 にグリッド切替を撤去) のスタイル」を共有できるので、
//   この差分の無い部分だけを本モジュールに切り出す。**構成 (タブの有無・グループ見出しの
//   有無) は各バリアント側の責務**であり、ここには持ち込まない。

import { type BrushSampleResult, renderBrushSample } from '@oekaki/drawing-core';
import type { PenParams, StrokePen, TexturePreset } from '@oekaki/protocol';

import type { PenPressureSettings } from '../../../application/penPressure.js';

/** サムネ canvas の論理解像度 (px)。BrushSamplePreview (220×40) より小さいパネル向け。 */
export const THUMB_W = 96 as const;
export const THUMB_H = 28 as const;

/**
 * ブラシパネル専用の airbrush プレビュー固定 seed。ストローク本番の seed とは無関係
 * (プレビューは wire に載らない使い捨て描画)。`ModernToolProperties.tsx` の
 * `PREVIEW_AIRBRUSH_SEED` と別定数にしている理由は無い (同じ値でも構わない) が、
 * パネル単体でテストしやすいよう自己完結させている。
 */
export const BRUSH_PANEL_AIRBRUSH_SEED = 0x6a17_ba51 as const;

/**
 * トーン item のサムネ専用の固定 pitch (phase11-light-pens-2026-08-21.md §4.5-§4.7、
 * 封印解除)。`penParams` を渡さないと `pitch` が省略され、drawing-core 側の
 * wire-omitted-field replay fallback (`TONE_DEFAULT_PITCH` = 4、pre-pitch レコードの
 * 忠実性のためだけの値) にフォールバックしてしまい、`4` は柄として成立せずサムネが
 * ノイズに見える (§4.3/§4.4)。UI 既定 (`DEFAULT_TONE_PITCH`) と同じ値を固定で渡し、
 * サムネが常に読める柄になるようにする (= crayon/pastel と違い「フォールバックのままで
 * 見た目が壊れない」が成立しないケースなので、texture/airbrush と同様に明示が必要)。
 */
const BRUSH_PANEL_TONE_PITCH_PREVIEW = 16 as const;

/**
 * 項目領域の高さ上限 (ドック全体を溢れさせず、パネル内で閉じ込めてスクロールさせる
 * ための対策、2026-08-19 導入)。
 *
 * タブ構成後 (§14.1): 表示されるのは常に 1 タブ分だけなので、今日の組込み項目数
 * (最大 4 = テクスチャタブ) では px 上限に達しない (= 保険として残るだけ)。将来
 * カスタムブラシで 1 タブの項目数が増えたときに効く。値の根拠は元の 300px 見積もり
 * (旧: 全7項目の list 表示見積もり) をそのまま流用している (タブ化で必要な上限は
 * むしろ下がったが、値を下げ直す積極的な理由が無いため現状維持)。
 *
 * フォン版 (`ModernBrushPanelFlat`) は全項目 (旧7項目) を並べるので、この上限が
 * 従来どおり効く (= 凍結対象、値を変えない)。
 */
export const BRUSH_PANEL_ITEMS_MAX_HEIGHT = 'min(300px, 45dvh)' as const;

/** 1 item = ボタン 1 個ぶん (= 描画に反映する pen + 任意で固定 texturePreset)。 */
export interface BrushPanelItem {
  readonly key: string;
  readonly label: string;
  readonly pen: Extract<
    StrokePen,
    'brush' | 'flat' | 'texture' | 'tone' | 'airbrush' | 'crayon' | 'pastel' | 'dot'
  >;
  /** texture item のときだけ、そのサムネ/クリックが対象とする固定 preset。 */
  readonly texturePreset?: TexturePreset;
}

/**
 * item の `pen` から使い方マニュアル (user-manual-2026-09-19.md §10.2) の
 * `data-manual` id を導く。`pen='texture'` は preset 問わず単一の `draw.texture`
 * に集約する (registry には preset ごとの項目が無いため)。
 */
export const manualIdForBrushPanelItem = (item: BrushPanelItem): string => {
  switch (item.pen) {
    case 'brush':
      return 'draw.brush';
    case 'flat':
      return 'draw.signpen';
    case 'dot':
      return 'draw.dot';
    case 'tone':
      return 'draw.tone';
    case 'airbrush':
      return 'draw.airbrush';
    case 'texture':
      return 'draw.texture';
    case 'crayon':
      return 'draw.crayon';
    case 'pastel':
      return 'draw.pastel';
  }
};

export interface ItemSample {
  readonly key: string;
  readonly sample: BrushSampleResult;
}

/** サムネ計算に必要な現在値 (太さ/不透明度/筆圧/airbrush パラメータ)。 */
export interface BrushPanelSampleInputs {
  readonly width: number;
  readonly alpha: number;
  readonly pressure: PenPressureSettings;
  readonly airbrushGrain: number;
  readonly airbrushDensity: number;
}

/**
 * 指定 item 群のサムネをまとめて計算する (= `renderBrushSample` の呼び出しをまとめる
 * だけの純粋関数、DOM 非依存)。呼び出し側 (各バリアント) が対象範囲 (全 item / アクティブ
 * タブの item だけ) を決めて渡す — **「計算対象をどう絞るか」はここでなく呼び出し側の
 * 責務** (デスクトップ版はアクティブタブぶんだけ、フォン版は全 item)。
 *
 * airbrush は `penParams` (seed/grain/density) を渡さないと `resolveStampRenderer` が
 * noop レンダラを返し空白になる (2026-08 発覚のバグ、`BrushSamplePreview` と同じ罠) ので、
 * 固定 seed + 現在の grain/density を必ず埋める。texture item は自分自身の preset を
 * 固定で渡す (= 4 item が互いに異なる絵になる、現在選択中の texturePreset の値には
 * 依存しない)。
 */
export const computeBrushPanelSamples = (
  items: ReadonlyArray<BrushPanelItem>,
  inputs: BrushPanelSampleInputs,
): ReadonlyArray<ItemSample> => {
  const penWidth = Math.max(1, Math.round(inputs.width));
  const pressureParam = {
    widthEnabled: inputs.pressure.widthEnabled,
    widthMin: inputs.pressure.widthMin,
    alphaEnabled: inputs.pressure.alphaEnabled,
    alphaMin: inputs.pressure.alphaMin,
  };
  return items.map((item) => {
    const penParams: PenParams | undefined =
      item.pen === 'airbrush'
        ? {
            kind: 'airbrush',
            seed: BRUSH_PANEL_AIRBRUSH_SEED,
            grain: inputs.airbrushGrain,
            density: inputs.airbrushDensity,
          }
        : item.texturePreset !== undefined
          ? { kind: 'texture', preset: item.texturePreset }
          : item.pen === 'tone'
            ? { kind: 'tone', pattern: 'dot', density: 0.5, pitch: BRUSH_PANEL_TONE_PITCH_PREVIEW }
            : undefined;
    const sample = renderBrushSample({
      width: THUMB_W,
      height: THUMB_H,
      pen: item.pen,
      penWidth,
      alpha: inputs.alpha,
      pressure: pressureParam,
      penParams,
    });
    return { key: item.key, sample };
  });
};

// リスト/グリッド切替の撤去 (2026-08-19、consult 不要の単純化):
// タブ化 (§14) で 1 タブあたり最大 4 item になり、グリッド表示の存在理由 (= 一覧が長い
// ときのコンパクトさ) が消えた。撤去してリスト表示に固定する (desktop/phone 両方)。
// `viewToggleClass` / `BrushPanelView` 依存の class 生成 (旧 `gridContainerClass` /
// 旧 `itemCanvasClass` の view 分岐) はここで削除済み。
//
// 将来の申し送り: カスタムブラシが入って「マイブラシ」タブが長くなったら、**そのタブの
// 中だけ**グリッド表示を再導入する余地がある (= タブ内への追加として additive に対応
// できる。パネル構造自体の作り直しは不要)。

// item のラベルをサンプル画像に重ねる (2026-08-19、狭ドックでの折返し対策):
// 旧構造は `flex flex-row items-center gap-2` の中で canvas に `w-full` を与えていた
// ため、canvas が行幅を丸ごと要求し、ラベルが残りわずかな幅に押し込まれて 1〜2 文字
// ずつ折り返す問題があった (狭ドックほど悪化)。「サンプルの上に名前を重ねる」構造に
// 作り直す: button を `relative` にして単一子 (canvas) だけを通常フローに置き、
// ラベルは `absolute` でその上に重ねる。これで canvas は常に button の全幅を素直に
// 使え、ラベルは button 全体を基準に位置指定できる (canvas の実サイズに依存しない)。
//
// **可読性**: サンプルは `renderBrushSample`/`compositeOverWhite` が「白地に黒い
// 筆跡」で描く。ラベルは黒文字 + 4方向白 text-shadow の「白ハロー」で縁取る
// (`NameLabelOverlay.tsx` の既存パターンを踏襲、半透明の白い下地は採らない —
// 下地だと帯の部分でサンプルの筆跡自体を隠してしまい、「サンプルに名前を重ねる」
// でなく「名前の周りだけサンプルが見える」逆転した見た目になるため)。黒文字 +
// 白ハローは筆跡 (黒系) にも地 (白) にも埋もれずコントラストが安定するので、
// active/inactive で文字色を出し分ける必要も無くなった (旧実装は active のとき
// だけ `text-black` にしていたが、ハロー方式ではどちらでも読める)。
//
// **`truncate`**: カスタムブラシ等で長い名前が来ても折り返さず省略する必須要件
// (Tailwind `truncate` = `overflow:hidden; text-overflow:ellipsis; white-space:nowrap`)。
// ラベルを `flex` コンテナにすると `text-overflow:ellipsis` が効かない罠があるため、
// 単一の `absolute` 要素 (= block 相当) に `inset-x-*` で確定幅を与え、`top-1/2
// -translate-y-1/2` で縦中央に置く (flex を使わない代わりに transform で中央寄せ)。
//
// **`pointer-events-none`**: ラベルの上をクリックしても下の `<button>` (canvas と
// 同じ要素) に click が通る。

/** item ボタン (リスト表示固定) の Tailwind class。ラベルの absolute 配置の基準に
 * なるため `relative` を持つ。 */
export const itemButtonClass = (active: boolean): string =>
  `relative rounded border p-1 transition-colors ${
    active
      ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/20'
      : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] hover:bg-[var(--color-bg)]'
  }`;

/** item 群を並べるコンテナの Tailwind class (リスト表示固定)。 */
export const ITEMS_CONTAINER_CLASS = 'flex flex-col gap-1' as const;

/** item canvas の Tailwind class (リスト表示固定)。高さは可読性のため旧 `h-6` から
 * `h-7` に上げた (ラベルをサンプル上に重ねる関係で、サンプルの縦幅が広いほど
 * 文字が筆跡と重なる面積の割合が減り読みやすくなるため)。 */
export const ITEM_CANVAS_CLASS = 'block h-7 w-full rounded-sm' as const;

/** item ラベル (サンプル画像に重ねる) の Tailwind class。黒文字 + 白ハロー
 * (text-shadow、下記 `ITEM_LABEL_TEXT_SHADOW`) で可読性を担保する。 */
// **位置は左下** (2026-08-18)。`renderBrushSample` の「〜」は**上下中央を軸に波打つ**
// ため、縦中央に置くと筆跡と最も重なる。波は左右の端で中央の高さを通り、山と谷は
// 途中で来るので、左下が相対的に空く。`inset-x-1.5` を残しているのは truncate に
// 確定幅が要るため (flex の子だと ellipsis が効かないので flex は使わない)。
export const ITEM_LABEL_CLASS =
  'pointer-events-none absolute inset-x-1.5 bottom-0.5 truncate select-none text-[10px] font-bold text-black' as const;

/** `ITEM_LABEL_CLASS` と対で使う白ハロー (4方向 + にじみ)。Tailwind に text-shadow
 * ユーティリティが無いため inline style で当てる (`NameLabelOverlay.tsx` と同じ
 * 手当て)。 */
export const ITEM_LABEL_TEXT_SHADOW =
  '-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff, 0 0 2px #fff' as const;

/** 両バリアント共通の props。 */
export interface ModernBrushPanelProps {
  /** 現在のペン種 (= どの item をハイライトするか、texture item は texturePreset も見る)。 */
  readonly pen: StrokePen;
  /** item クリックで発火 (= 共有 chrome の `onSelectPen`)。 */
  readonly onSelectPen: (
    pen: Extract<
      StrokePen,
      'brush' | 'flat' | 'texture' | 'tone' | 'airbrush' | 'crayon' | 'pastel' | 'dot'
    >,
  ) => void;
  /**
   * texture item クリックで発火 (= 共有 chrome の `onSelectTexturePreset`)。texture item
   * は `onSelectPen('texture')` とあわせて 1 クリックで両方呼ぶ (§3 の1クリック要件)。
   */
  readonly onSelectTexturePreset: (preset: TexturePreset) => void;
  /** サムネに反映する現在の太さ (= ツールプロパティの太さと同一値)。 */
  readonly width: number;
  /** サムネに反映する現在の不透明度 (0..255)。 */
  readonly alpha: number;
  /** サムネに反映する現在の筆圧設定。 */
  readonly pressure: PenPressureSettings;
  /** 現在の texture preset (= texture item のうちどれをハイライトするかの判定)。 */
  readonly texturePreset: TexturePreset;
  /** エアブラシの粒の粗さ (airbrush item のサムネに反映必須、§4 参照)。 */
  readonly airbrushGrain: number;
  /** エアブラシの噴霧密度 (airbrush item のサムネに反映必須)。 */
  readonly airbrushDensity: number;
}
